import { z } from 'zod'
import { wallClockSchema } from '../validators/temporal.validator.js'

/**
 * Whether a lane is free for a window.
 *
 * **`unknown` is a real answer, not a missing one.** An event with no date holds
 * no lane and contends with nothing — `windowsOverlap` says so in both
 * directions — so there is no moment to ask about and "free" would be a claim
 * nobody made. The picker shows the lane uncolored rather than green.
 */
export const trackAvailabilityStatusSchema = z.enum(['free', 'taken', 'unknown'])

/** Which lane is being answered about. Common to every answer. */
const laneShape = {
  trackId: z.string().uuid(),
  trackName: z.string(),
} as const

/**
 * One lane's answer.
 *
 * **A union, not a status beside a nullable `takenBy`.** Those two fields could
 * disagree — `free` with an event attached, or `taken` with nobody — and the
 * only reason they never did was that one function built them together. The
 * consumer paid for it: the picker wrote `answer.takenBy ? … : ''`, a branch for
 * a state the server does not produce, and would have rendered "Taken" with no
 * name if it ever did.
 *
 * The same fix `addEventClientInputSchema` records: a refine or a convention
 * tells a caller something and tells the compiler nothing. Narrowing on
 * `status === 'taken'` now *gives* you `takenBy`.
 *
 * One literal per branch rather than `z.enum(['free','unknown'])` on a shared
 * one: an enum-typed field narrows the *field* and not the member, so the
 * compiler still refused `takenBy` after both other statuses had been ruled
 * out. A discriminated union narrows the whole shape.
 */
export const trackAvailabilityDtoSchema = z.discriminatedUnion('status', [
  /** Free for the window asked about. */
  z.object({ ...laneShape, status: z.literal('free') }),
  /** Nothing was asked, because there is no window to judge. */
  z.object({ ...laneShape, status: z.literal('unknown') }),
  z.object({
    ...laneShape,
    status: z.literal('taken'),
    /**
     * The event holding the lane. Named, because "this lane is taken" is not
     * actionable at a venue with several — the same reason the booking refusal
     * names both ends.
     */
    takenBy: z.object({
      eventId: z.string().uuid(),
      eventName: z.string(),
    }),
  }),
])

export type TrackAvailabilityDto = z.infer<typeof trackAvailabilityDtoSchema>

/**
 * Every lane at a venue, for the window that was asked about.
 *
 * **The window comes back because it is not always the one that was sent.**
 * Asking with a date and no times is answered for the window a booking would
 * actually take — the venue's usual evening — so without this a caller cannot
 * tell what its answer is about, and could show "taken" against times the
 * server never considered. Returned for that reason rather than for display.
 */
export const trackAvailabilityListDtoSchema = z.object({
  tracks: z.array(trackAvailabilityDtoSchema),
  /** Null when no date was asked about, which is what makes every lane unknown. */
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
})

export type TrackAvailabilityListDto = z.infer<typeof trackAvailabilityListDtoSchema>
